import type { JSX } from 'preact';
import { session } from '../lib/session';
import { useResource } from '../lib/store';
import { LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { clinicToday } from '../lib/clinicDay';
import { visibleClinics } from './ClinicTabs';
import {
  SERVICE_COMPLIANCE_KEY, ALERT_DAYS, daysUntilAlert, isDueSoon, isoToMd,
  fetchServiceCompliance, type ServiceComplianceData, type ServiceComplianceRow,
} from '../lib/serviceCompliance';
import {
  LICENSES_KEY, daysUntilExpiry, isExpiringSoon, isOldLicense, fetchLicenses, type LicensesData, type LicenseRow,
} from '../lib/licenses';
import { DeskPanel } from './DeskPanel';

function canSee(role: string | null | undefined): boolean {
  return role === 'OWNER' || role === 'DIRECTOR' || role === 'MANAGER';
}

function timing(days: number): string {
  if (days < 0) return `overdue ${-days} day${-days === 1 ? '' : 's'}`;
  if (days === 0) return 'due today';
  return `due in ${days} days`;
}

interface SpcAlertItem { kind: 'spc'; days: number; label: string; row: ServiceComplianceRow }
interface LicenseAlertItem { kind: 'license'; days: number; label: string; row: LicenseRow }
type AlertItem = SpcAlertItem | LicenseAlertItem;

export function ComplianceAlerts(
  { now = new Date(), chromeless = false }: { now?: Date; chromeless?: boolean } = {},
): JSX.Element | null {
  const canView = canSee(session.value.me?.role);
  // Same loader ServiceCompliance.tsx (the full SPC page) uses —
  // `useResource`, not `peekResource`: this is what makes the panel fetch
  // (or revalidate a stale mirrored copy) on its OWN mount instead of only
  // ever showing whatever bootstrap happened to have already cached. Hooks
  // run every render regardless of the role gate below (rules of hooks),
  // same discipline as RaiseAlerts.
  const load = useResource<ServiceComplianceData>(
    canView ? SERVICE_COMPLIANCE_KEY : null,
    fetchServiceCompliance,
  );
  // N-094 ("forward and file"): "License expiring" joins this same alert
  // list, off the same role/clinic gate as SPC — OWNER/DIRECTOR see every
  // visible clinic, MANAGER sees only their own, exactly what
  // `visibleClinics()` already encodes below.
  const licenseLoad = useResource<LicensesData>(
    canView ? LICENSES_KEY : null,
    fetchLicenses,
  );
  if (!canView) return null;
  const data = load.status === 'ready' ? load.data : null;
  const licenseData = licenseLoad.status === 'ready' ? licenseLoad.data : null;
  const today = clinicToday(now);
  const clinics = new Set(visibleClinics());
  const showClinic = clinics.size > 1;

  const spcItems: AlertItem[] = (data?.rows ?? [])
    .filter((r) => clinics.has(r.locationCode as LocationCode) && isDueSoon(r, today))
    .map((row) => ({ kind: 'spc', days: daysUntilAlert(row, today) ?? 99, label: row.fullName, row }));

  const licenseItems: AlertItem[] = (licenseData?.rows ?? [])
    // N-100: a license moved to Old Licenses is archived, not current — an
    // expired one sitting there must not alert (owner, 2026-09-10).
    .filter((r) => clinics.has(r.locationCode as LocationCode) && !isOldLicense(r) && isExpiringSoon(r, today))
    .map((row) => ({ kind: 'license', days: daysUntilExpiry(row, today) ?? 99, label: row.kind, row }));

  // Merge-sorted by days, not appended list-after-list — a license expiring
  // tomorrow outranks an SPC item due in three weeks.
  const items = [...spcItems, ...licenseItems].sort((a, b) => a.days - b.days || a.label.localeCompare(b.label));

  if (!items.length) {
    // See RaiseAlerts.tsx's N-090c comment: DeskGrid can mount this panel
    // BEFORE it knows whether there is anything due (panelCatalog.tsx's
    // spcAlertsHasContent / store.ts's resourcePendingFetch) — name the
    // empty state rather than vanish once the fetch resolves empty. Outside
    // the canvas, keep returning null (no dedicated standalone test today,
    // but nothing else has ever rendered this component un-gated).
    //
    // Text deliberately left SPC-only (not "SPC or license") — this exact
    // string is pinned by desk-grid.test.tsx against panelCatalog.tsx's own
    // `emptyLabel`, which a license-expiring item never changes because
    // `spcAlertsHasContent` treats "nothing SPC AND nothing license due" the
    // same "nothing to show" case either way.
    if (!chromeless) return null;
    return (
      <DeskPanel title="SPC" testId="compliance-alerts-panel" chromeless>
        <p class="empty">{`No SPC items due within ${ALERT_DAYS} days.`}</p>
      </DeskPanel>
    );
  }

  return (
    <DeskPanel
      title="SPC"
      subtitle="SPC and licenses due within 45 days."
      testId="compliance-alerts-panel"
      chromeless={chromeless}
    >
      {/* Owner, 2026-10-12: rows read like My Mail — the person (or
         "License expiring") bold, the detail muted underneath, the timing
         small on the right. A one-clinic viewer (a manager) sees no clinic
         name at all — it is their clinic; owner/director see it AFTER the
         name ("Jahaira Ayala · Lake Mary"), not as a left label column. */}
      <ul class="desk-list desk-panel-scroll" data-testid="compliance-alerts">
        {items.map((item) => {
          const where = showClinic
            ? ` · ${LOCATION_NAMES[item.row.locationCode as LocationCode] ?? item.row.locationCode}`
            : '';
          const rowClass = `desk-row compliance-alert ${item.days < 0 ? 'overdue is-overdue' : 'is-warn'}`;
          return item.kind === 'spc' ? (
            <li class={rowClass} key={`spc-${item.row.id}`} data-testid={`compliance-alert-spc-${item.row.id}`}>
              <span class="desk-row-line">
                <span class="desk-row-main">{item.row.fullName}{where}</span>
                <span class="desk-row-value">{timing(item.days)}</span>
              </span>
              <span class="desk-row-sub">
                {item.row.verificationDetail || item.row.verificationType}
                {' · '}
                {isoToMd(item.row.dueDate || item.row.expirationDate || item.row.renewalDate)}
              </span>
            </li>
          ) : (
            <li class={rowClass} key={`license-${item.row.id}`} data-testid={`compliance-alert-license-${item.row.id}`}>
              <span class="desk-row-line">
                <span class="desk-row-main">License expiring{where}</span>
                <span class="desk-row-value">{timing(item.days)}</span>
              </span>
              <span class="desk-row-sub">
                {item.row.kind}
                {item.row.issuer ? ` · ${item.row.issuer}` : ''}
                {' · expires '}
                {isoToMd(item.row.expiresOn)}
              </span>
            </li>
          );
        })}
      </ul>
    </DeskPanel>
  );
}
